import { lazy, Suspense } from 'react';
import { Outlet, Navigate, useRoutes } from 'react-router-dom';

import { MorphingSquare } from '@/components/ui/morphing-square';

import { useI18n } from 'src/i18n';
import { AuthLayout } from 'src/layouts/auth';
import { DashboardLayout } from 'src/layouts/dashboard';
import { AuthGuard } from 'src/features/auth/components/auth-guard';
import { AdminGuard } from 'src/features/auth/components/admin-guard';

// ----------------------------------------------------------------------

export const LandingPage = lazy(() => import('src/features/landing/routes/landing-page'));
export const HomePage = lazy(() => import('src/features/dashboard/routes/home-page'));
export const BlogPage = lazy(() => import('src/features/dashboard/routes/blog-page'));
export const UserPage = lazy(() => import('src/features/admin/routes/user-page'));
export const SignInPage = lazy(() => import('src/features/auth/routes/sign-in-page'));
export const Page404 = lazy(() => import('src/app/routes/page-not-found-page'));
export const RegisterPage = lazy(() => import('src/features/auth/routes/register-page'));
export const ForgotPasswordPage = lazy(() => import('src/features/auth/routes/forgot-password-page'));
export const JobsPage = lazy(() => import('src/features/jobs/routes/jobs-page'));
export const JobDetailPage = lazy(() => import('src/features/jobs/routes/job-detail-page'));
export const JobMarketPage = lazy(() => import('src/features/job-market/routes/job-market-page'));
export const ProfilePage = lazy(() => import('src/features/profile/routes/profile-page'));
export const OneMinPortfolioPage = lazy(
  () => import('src/features/portfolio/routes/one-min-portfolio-page')
);
export const ExtensionUpdatePage = lazy(() => import('src/app/routes/extension-update-page'));
export const ExtensionAuthPage = lazy(() => import('src/features/auth/routes/extension-auth-page'));
export const PortfolioPage = lazy(() => import('src/features/portfolio/routes/portfolio-page'));
export const SubscriptionPage = lazy(() => import('src/features/subscription/routes/subscription-page'));
export const SubscriptionConfirmingPage = lazy(
  () => import('src/features/subscription/routes/subscription-confirming-page')
);
export const TreeHolePage = lazy(() => import('src/features/community/routes/comments-page'));
export const SingleCommentPage = lazy(
  () => import('src/features/community/routes/single-comment-page')
);
export const GlobalDashboardPage = lazy(() => import('src/features/admin/routes/global-dashboard-page'));
export const JobicornPage = lazy(() => import('src/app/routes/jobicorn-page'));
export const ReportsPage = lazy(() => import('src/features/reports/routes/reports-page'));
export const CoffeeChatsPage = lazy(() => import('src/features/coffee-chat/routes/coffee-chats-page'));
export const CoffeeChatInviteLandingPage = lazy(() => import('src/features/coffee-chat/routes/coffee-chat-invite-landing-page'));
export const McpSetupPage = lazy(() => import('src/app/routes/mcp-setup-page'));
export const OAuthConsentPage = lazy(() => import('src/features/mcp/routes/oauth-consent-page'));
export const AILabPage = lazy(() => import('src/features/ai-tools/routes/ai-lab-page'));
export const DocsPage = lazy(() => import('src/features/docs/routes/docs-page'));

export function Router() {
  const { t } = useI18n();
  const renderFallback = (
    <div className="flex flex-1 min-h-[50vh] items-center justify-center flex-col gap-4">
      <div className="relative flex items-center justify-center">
        <MorphingSquare message={t('router.loading')} />
      </div>
    </div>
  );

  return useRoutes([
    // Public landing page (no auth required)
    {
      element: (
        <Suspense fallback={renderFallback}>
          <LandingPage />
        </Suspense>
      ),
      index: true,
    },
    {
      element: (
        <AuthGuard>
          <DashboardLayout>
            <Suspense fallback={renderFallback}>
              <Outlet />
            </Suspense>
          </DashboardLayout>
        </AuthGuard>
      ),
      children: [
        { element: <HomePage />, path: 'dashboard' },
        { path: 'saved-jobs', element: <JobsPage /> },
        { path: 'saved-jobs/:jobId', element: <JobDetailPage /> },
        { path: 'job-market', element: <JobMarketPage /> },
        { path: 'user', element: <UserPage /> },
        { path: 'profile', element: <ProfilePage /> },
        { path: 'one-min-portfolio', element: <OneMinPortfolioPage /> },
        { path: 'blog', element: <BlogPage /> },
        { path: 'subscription', element: <SubscriptionPage /> },
        {
          path: 'globaldashboard',
          element: (
            <AdminGuard>
              <GlobalDashboardPage />
            </AdminGuard>
          ),
        },
        { path: 'balance', element: <Navigate to="/subscription" replace /> },
        { path: 'balance/top-up', element: <Navigate to="/subscription" replace /> },
        { path: 'balance/history', element: <Navigate to="/payment-history" replace /> },
        {
          path: 'balance/transaction-history',
          element: <Navigate to="/payment-history" replace />,
        },
        { path: 'tree-hole', element: <TreeHolePage /> },
        { path: 'tree-hole/:commentId', element: <SingleCommentPage /> },
        { path: 'jobicorn', element: <JobicornPage /> },
        { path: 'reports', element: <ReportsPage /> },
        { path: 'coffee-chats', element: <CoffeeChatsPage /> },
        { path: 'ai-lab', element: <AILabPage /> },
        { path: 'mcp-setup', element: <McpSetupPage /> },
        { path: 'oauth/authorize/consent', element: <OAuthConsentPage /> },
        {
          path: 'subscription-confirming',
          element: (
            <Suspense fallback={renderFallback}>
              <SubscriptionConfirmingPage />
            </Suspense>
          ),
        },
      ],
    },

    {
      path: 'portfolio/:id',
      element: (
        <Suspense fallback={renderFallback}>
          <PortfolioPage />
        </Suspense>
      ),
    },
    {
      path: 'coffee-chat/invite/:token',
      element: (
        <Suspense fallback={renderFallback}>
          <CoffeeChatInviteLandingPage />
        </Suspense>
      ),
    },
    {
      path: 'docs',
      element: (
        <Suspense fallback={renderFallback}>
          <DocsPage />
        </Suspense>
      ),
    },
    {
      path: 'sign-in',
      element: (
        <AuthLayout>
          <SignInPage />
        </AuthLayout>
      ),
    },
    {
      path: '404',
      element: <Page404 />,
    },
    {
      path: 'register',
      element: (
        <AuthLayout>
          <RegisterPage />
        </AuthLayout>
      ),
    },
    {
      path: 'extension-update',
      element: <ExtensionUpdatePage />,
    },
    {
      path: 'extension-auth',
      element: <ExtensionAuthPage />,
    },
    {
      path: 'forgot-password',
      element: (
        <AuthLayout>
          <ForgotPasswordPage />
        </AuthLayout>
      ),
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);
}
